import { Component, OnInit, TemplateRef } from '@angular/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { filtrarPorEmpresa } from 'src/app/pipes/empresa-cuenta.pipe';
import { DecimalPipe, DatePipe } from '@angular/common';
import * as alertifyjs from 'alertifyjs';
import Swal from 'sweetalert2';

import { CreditNoteService } from 'src/app/services/credit-note.service';
import { UserService } from 'src/app/services/user/user.service';

/**
 * Nota de Crédito — aplica una devolución a un crédito específico del cliente.
 *
 * Componente NUEVO. Sigue el mismo lenguaje visual que "Nuevo pago"
 * (components/post): tarjetas con cabecera degradada, utilidades de Bootstrap 5
 * e iconos FontAwesome. No reutiliza otros-flujos-pago (3.925 líneas) porque
 * este flujo no pide datos bancarios ni referencia.
 */
@Component({
  selector: 'app-nota-credito',
  templateUrl: './nota-credito.component.html',
  styleUrls: ['./nota-credito.component.css'],
  providers: [DecimalPipe, DatePipe],
})
export class NotaCreditoComponent implements OnInit {

  code_person: string = '';
  loading = true;
  saving = false;

  person: any = null;
  personFull: any = null;
  invoices: any[] = [];
  accounts: any[] = [];
  // Filtro de empresa de las cuentas (solo visual)
  empresaFiltro: 'CrediTotal' | 'Mundo Total' | 'Todas' = 'Todas';
  get cuentasVisibles(): any[] { return filtrarPorEmpresa(this.accounts, this.empresaFiltro); }

  // --- formulario ---
  selectedInvoiceCode: string = '';
  searchTerm: string = '';
  filteredInvoices: any[] = [];
  selectedAccountCode: string = '';
  montoBs: number | null = null;
  observaciones: string = '';
  archivo: File | null = null;
  fileName: string = '';

  // --- fecha, con el mismo patrón de chips que "Nuevo pago" ---
  dateOption: 'today' | 'yesterday' | 'custom' = 'today';
  fecha: string = '';

  // --- tasa ---
  rate: any = null;
  rateError = false;
  loadingRate = false;

  resultado: any = null;

  /** 1 = formulario, 2 = comprobante. Mismo patrón de vistas que el registro de pago. */
  selectView: 1 | 2 = 1;

  /** Instantánea de lo enviado, para que el comprobante no dependa del formulario ya limpiado. */
  comprobante: any = null;

  /**
   * Llave de idempotencia: una por intento. El backend la usa como candado, así
   * que un doble clic o un reintento de red no puede registrar y distribuir dos
   * veces. Se renueva sólo cuando la nota anterior se aplicó de verdad.
   */
  idempotencyKey: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _creditNote: CreditNoteService,
    private _user: UserService,
    private sanitizer: DomSanitizer,
    private offcanvasService: NgbOffcanvas
  ) { }

  async ngOnInit() {
    this.code_person = this.route.snapshot.paramMap.get('code_person') || '';
    this.setDateOption('today');
    this.nuevaLlave();
    await this.cargar();
  }

  nuevaLlave() {
    const c: any = (globalThis as any)?.crypto;
    this.idempotencyKey = c?.randomUUID
      ? c.randomUUID()
      : `nc-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  isoDe(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  setDateOption(op: 'today' | 'yesterday' | 'custom') {
    this.dateOption = op;
    const d = new Date();
    if (op === 'yesterday') d.setDate(d.getDate() - 1);
    if (op !== 'custom') this.fecha = this.isoDe(d);
    this.cargarTasa();
  }

  onFechaChange() {
    this.cargarTasa();
  }

  /** La tasa depende de la fecha del documento: es lo que convierte Bs a USD. */
  async cargarTasa() {
    if (!this.fecha) return;
    this.loadingRate = true;
    this.rateError = false;
    try {
      const resp = await this._user.getRate({ date: this.fecha, subscriptor: 1 });
      this.rate = resp?.res || null;
      if (!this.rate) this.rateError = true;
    } catch (e) {
      this.rateError = true;
    } finally {
      this.loadingRate = false;
    }
  }

  async cargar() {
    this.loading = true;
    try {
      const [inv, acc] = await Promise.all([
        this._creditNote.getPendingInvoices(this.code_person),
        this._creditNote.getAccounts()
      ]);

      this.person = inv?.person || null;

      // Ficha completa para el encabezado: misma fuente que usa el perfil del
      // cliente, para que la foto y los datos se vean idénticos.
      try {
        const full = await this._user.getPersonbyCode({ code: this.code_person });
        this.personFull = full?.resp || null;
      } catch (_) { this.personFull = null; }
      this.invoices = inv?.invoices || [];
      this.filteredInvoices = [...this.invoices];
      this.accounts = acc?.res || [];

      // Con una sola cuenta habilitada la preseleccionamos: el ejecutivo no tiene
      // que elegir nada que no sea una decisión real.
      if (this.accounts.length === 1) this.selectedAccountCode = this.accounts[0].code;

    } catch (e: any) {
      alertifyjs.error(e?.error?.error?.msg || 'No se pudieron cargar los datos del cliente');
    } finally {
      this.loading = false;
    }
  }

  sanitize(url: string) {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  // Mismos helpers que contact-profile, para que la ficha se lea igual.
  getIdentity(item: any) {
    if (!(item?.identitys?.length > 0)) return '';
    return `${item?.identitys[0]?.type_identity?.name} ${item?.identitys[0]?.code_identity?.name}-${item?.identitys[0]?.identity}`;
  }

  getContact(item: any) {
    if (!(item?.constacts?.length > 0)) return '';
    return `${item?.constacts[0]?.contact}`;
  }

  getEmailContact(item: any) {
    if (!(item?.constacts?.length > 0) || !item?.constacts[1]) return '';
    return `${item?.constacts[1]?.contact}`;
  }

  /** Panel lateral de selección, igual que el de "Seleccione la cuenta destino". */
  openview(content: TemplateRef<any>) {
    this.searchTerm = '';
    this.filterInvoices();
    this.offcanvasService.open(content, { position: 'start', keyboard: false });
  }

  filterInvoices() {
    const t = (this.searchTerm || '').trim().toLowerCase();
    if (!t) { this.filteredInvoices = [...this.invoices]; return; }
    this.filteredInvoices = this.invoices.filter(i =>
      `${i.identifier || ''} ${i.number || ''} ${i.dueBalance || ''}`.toLowerCase().includes(t)
    );
  }

  selectInvoice(item: any) {
    this.selectedInvoiceCode = item?.code || '';
    this.offcanvasService.dismiss();
  }

  identificador(i: any): string {
    return i?.identifier || (i?.number ? `CREDIT-${i.number}` : '');
  }

  irAPerfil() {
    this.router.navigate([`/console/person/profile/${this.code_person}`], {});
  }

  /** Tope real: lo que se debe en ESE crédito. */
  get maxUSD(): number | null {
    const s = this.invoiceSeleccionado?.dueBalance;
    return (s === undefined || s === null) ? null : s;
  }

  get maxBs(): number | null {
    if (this.maxUSD === null || !this.rate?.amount) return null;
    return Math.floor(this.maxUSD * this.rate.amount * 100) / 100;
  }

  get invoiceSeleccionado(): any {
    return this.invoices.find(i => i.code === this.selectedInvoiceCode) || null;
  }

  get cuentaSeleccionada(): any {
    return this.accounts.find(a => a.code === this.selectedAccountCode) || null;
  }

  get monedaCuenta(): string {
    return this.cuentaSeleccionada?.currency?.code || 'BS';
  }

  /** Equivalente en USD del monto capturado, con la tasa de la fecha elegida. */
  get montoUSD(): number | null {
    if (!this.montoBs) return null;
    if (this.monedaCuenta !== 'BS') return this.montoBs;
    if (!this.rate?.amount) return null;
    return Math.round((this.montoBs / this.rate.amount) * 100) / 100;
  }

  /**
   * El backend aplica el tope real, pero avisamos antes para no hacerle perder
   * el viaje al ejecutivo: la nota nunca puede exceder el saldo del crédito.
   */
  get excedeSaldo(): boolean {
    const usd = this.montoUSD;
    const saldo = this.invoiceSeleccionado?.dueBalance;
    return usd !== null && saldo !== undefined && usd > saldo;
  }

  onFile(event: any) {
    const f = event?.target?.files?.[0];
    if (!f) { this.archivo = null; this.fileName = ''; return; }

    const ext = f.name.split('.').pop()?.toLowerCase();
    if (!['png', 'jpg', 'jpeg', 'pdf'].includes(ext || '')) {
      alertifyjs.error('Solo se permiten archivos .png, .jpg, .jpeg o .pdf');
      event.target.value = '';
      return;
    }
    this.archivo = f;
    this.fileName = f.name;
  }

  get formValido(): boolean {
    return !!this.selectedInvoiceCode
      && !!this.selectedAccountCode
      && !!this.fecha
      && !!this.montoBs && this.montoBs > 0
      && !!this.observaciones?.trim()
      && !this.excedeSaldo;
  }

  async registrar() {
    if (!this.formValido || this.saving) return;

    const inv = this.invoiceSeleccionado;
    const usd = this.montoUSD;

    const confirm = await Swal.fire({
      title: '¿Aplicar la nota de crédito?',
      html: `
        <div style="text-align:left;font-size:14px">
          <p class="mb-1"><b>Cliente:</b> ${this.person?.name || ''} ${this.person?.lastName || ''}</p>
          <p class="mb-1"><b>Crédito:</b> ${inv?.identifier || ('CREDIT-' + inv?.number)} — saldo ${inv?.dueBalance} USD</p>
          <p class="mb-1"><b>Monto:</b> ${this.montoBs} ${this.monedaCuenta}${usd !== null ? ` (${usd} USD)` : ''}</p>
          <p style="margin-top:12px;color:#b45309">
            Se aplicará <b>únicamente</b> a las cuotas de este crédito y se
            desactivarán las penalidades de las cuotas que reciban el pago.
          </p>
        </div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, aplicar',
      cancelButtonText: 'Cancelar'
    });

    if (!confirm.isConfirmed) return;

    this.saving = true;
    this.resultado = null;

    try {
      const resp = await this._creditNote.register({
        code_person: this.code_person,
        code_invoice: this.selectedInvoiceCode,
        code_account_payment: this.selectedAccountCode,
        total_amount: this.montoBs,
        date_register: this.fecha,
        description: this.observaciones.trim(),
        idempotency_key: this.idempotencyKey
      }, this.archivo);

      this.resultado = resp;

      if (resp?.duplicate) {
        // El backend detectó que esta misma nota ya se había aplicado y devolvió
        // el resultado original en vez de registrarla otra vez.
        alertifyjs.warning('Esta nota de crédito ya había sido aplicada. No se registró de nuevo.');
      } else {
        alertifyjs.success('Nota de crédito aplicada');
      }

      // Instantánea antes de limpiar el formulario.
      this.comprobante = {
        ...resp,
        cuenta: this.cuentaSeleccionada,
        observaciones: this.observaciones.trim(),
        fecha: this.fecha,
        archivo: this.fileName,
        duplicate: !!resp?.duplicate
      };

      // Llave nueva: la siguiente nota es una operación distinta.
      this.nuevaLlave();

      await this.cargar();
      this.limpiar();

      // Comprobante en su propia vista, no colgando debajo del formulario.
      this.selectView = 2;
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (e: any) {
      const msg = e?.error?.error?.msg || 'No se pudo aplicar la nota de crédito';

      if (e?.status === 409) {
        // Doble clic: hay otra petición idéntica en vuelo. NO renovamos la llave,
        // para que un reintento siga topando con el mismo candado.
        Swal.fire({ title: 'Operación en curso', text: msg, icon: 'info' });
      } else {
        Swal.fire({ title: 'No se aplicó', text: msg, icon: 'error' });
      }
    } finally {
      this.saving = false;
    }
  }

  limpiar() {
    this.selectedInvoiceCode = '';
    this.montoBs = null;
    this.observaciones = '';
    this.archivo = null;
    this.fileName = '';
  }

  nuevaNota() {
    this.comprobante = null;
    this.resultado = null;
    this.selectView = 1;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  irAPagos() {
    this.router.navigate([`/console/payments/person/${this.code_person}`], {});
  }

  volver() {
    this.router.navigate([`/console/person/profile/${this.code_person}`]);
  }
}
